/**
 * Colours from the prototype mockup provided
 * Tokens here do not exist on current design-system package
 *
 * Delete this file when the design system ships v2 colours in its entirety.
 */
export const v2Colors = {
  /** `--zai-purple-600` — active nav item text, v2 avatar glyph. */
  purple600: '#552CA0',
  /** `--ink-2` — resting nav item text, topbar marketplace name. */
  ink2: '#3C3F52',
  /** `--faint` — nav group labels, resting `AuthField` label. */
  faint: '#8A8CA0',
  /** `--line` — sidebar and topbar borders, auth screen hairlines. */
  line: '#E6E4EE',
  /** `--ink` — headings on the light panel. */
  ink: '#1B1E2B',
  /** `--muted` — secondary copy, unselected segment labels. */
  muted: '#5A5D75',
  /** `--plane` — the segmented control's trough. */
  plane: '#F7F6FA',
  /** `--line-soft` — table row dividers, accordion column rules. */
  lineSoft: '#F0EEF5',
  /** `--zai-purple` — open-row chevron, Columns menu tick. */
  purple: '#6738BD',
  /** `--tint` — selected chip background. */
  tint: '#EFEBF7',
  /** Mockup `th` background. */
  tableHead: '#FCFBFE',
  /** Mockup row hover and open-row background (`tbody tr:hover`, `tr.det>td`). */
  rowTint: '#FBFAFE',
  /** Mockup `.axis` — the chart's baseline. */
  axis: '#D7D4E2',
  /** Focus halo around a v2 field: `$violet900` at 13% alpha. */
  focusRing: 'rgba(103, 56, 189, .13)',
  /** Focus halo around a v2 field in error: `--critical` at 16% alpha. */
  errorRing: 'rgba(208, 59, 59, .16)',
  /** Zai brand red — the gradient's second stop. Zai purple is the DS `$violet900`. */
  red: '#EE3932',
} as const;

/**
 * The brand gradient
 * Usable in any Stitches style; `$colors$violet900` resolves to the DS token.
 */
export const v2Gradients = {
  brand: `linear-gradient(45deg, $colors$violet900 0%, ${v2Colors.red} 100%)`,
} as const;

/** Lift under the primary auth button: `$violet900` at 32% / 46% alpha. */
export const v2Shadows = {
  button: '0 6px 18px rgba(103, 56, 189, .32)',
  buttonHover: '0 10px 26px rgba(103, 56, 189, .46)',
} as const;

/** The DS has no motion scale. */
export const v2Durations = {
  /** Press feedback. */
  fast: '100ms',
  /** Hover fades and shadow changes. */
  base: '200ms',
} as const;

/*
 * Non-colour values the v2 screens use that have no design-system equivalent
 * (the DS scales are listed beside each group). Where the DS does have the
 * value — e.g. 16px is `$4`, 0.02em is `$wide` — use the DS token instead.
 */

/** DS radii: 2, 4, 8, 16, 24px. */
export const v2Radii = {
  /** Auth fields, the auth button and the environment segment. */
  control: '11px',
  /** Small controls nested inside a field, e.g. the password reveal. */
  inset: '7px',
} as const;

/** DS font weights: 400, 500, 700. */
export const v2FontWeights = {
  semibold: 600,
  heavy: 650,
} as const;

/** DS font sizes: 12, 14, 16, 18, 24px… */
export const v2FontSizes = {
  /** A field's label once it has floated above the value. */
  label: '11px',
  /** Small inline controls, e.g. the password reveal. */
  small: '12.5px',
  /** Text typed into an auth field, and its resting label. */
  input: '14.5px',
  /** The primary auth button's label. */
  button: '15px',
  /** Table cells, toolbar controls, pills and menus. */
  body: '13px',
  /** Page `h1` (mockup `h1`). */
  pageTitle: '22px',
  /** Summary figures, e.g. total volume (mockup `.sumfig .v`). */
  figure: '30px',
  /** Chart axis labels (mockup `.tick`). */
  tick: '11px',
} as const;

/** DS fonts: `$base` (Poppins) only. */
export const v2Fonts = {
  /** IDs and timestamps in tables (mockup `.mono`). */
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

/** Geometry of the v2 volume chart and its summary band (mockup `.sumband`, `miniBars`). */
export const v2VolumeChart = {
  /** Width of the figures column beside the chart. */
  figuresWidth: '270px',
  height: '180px',
  /** Recharts takes numbers for these. */
  barMaxWidth: 30,
  barRadius: 4,
  yAxisWidth: 52,
  tickGap: 8,
} as const;

/** DS sizes step 48px → 56px. */
export const v2Sizes = {
  /** Auth field height. */
  field: '52px',
  /** Primary auth button height. */
  button: '50px',
  /** Auth field border; the DS has no border-width scale. */
  fieldBorder: '1.5px',
} as const;
